import { FormSchema } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { InspectionTemplate } from '@stormwater-software/lib-inspection-templates';
import { find } from 'lodash';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';
import { State } from '../../../utils/api/address';
import { Client } from '../../../utils/api/client';
import validator from '../../../utils/FormValidator';
import { env } from '../../../config';

const halfWidthStyle = { flex: '1 1 50%', alignSelf: 'flex-end' };
const emptyDescriptionStyle = { marginTop: '1.5em' };

export type ProjectConfig = 'full' | 'e_swp' | 'it' | 'it_eswp' | 'map' | 'it_map';

export function getProjectConfigHelpers(projectConfig: ProjectConfig = 'full') {
  const hasInAppInspections = projectConfig === 'full';
  const hasExternalTracking = (['it', 'it_eswp', 'it_map'] as ProjectConfig[]).includes(projectConfig);
  // hasMapManagement: can edit/annotate the site map and SWP data sources
  const hasMapManagement = (['full', 'e_swp', 'it_eswp', 'map', 'it_map'] as ProjectConfig[]).includes(projectConfig);
  // hasDocEditing: can use PDF/Apryse annotation tools
  const hasDocEditing = (['full', 'e_swp', 'it_eswp'] as ProjectConfig[]).includes(projectConfig);
  return {
    hasMapManagement,
    hasDocEditing,
    hasDocManagement: hasMapManagement, // alias — use hasDocEditing for PDF gating
    hasInAppInspections,
    hasExternalTracking,
    hasAnyInspections: hasInAppInspections || hasExternalTracking,
  };
}

export const PROJECT_CONFIG_LABELS: Record<ProjectConfig, string> = {
  full: 'Full Project',
  e_swp: 'E-SWP',
  it: 'Inspection Tracking',
  it_eswp: 'Inspection Tracking + E-SWP',
  map: 'Map',
  it_map: 'Inspection Tracking + Map',
};

export const projectInspectorUserRoles = [
  'Admin',
  'Inspector',
  'Area / Assistant Manager',
  'Regional Manager',
  'SWMP Admin',
  'CDOT PE/Designee',
  'Supervising Engineer',
  'Client Inspector',
  'Client Manager',
  'Divisions Admin',
  'Regional Manager (External)',
];

const systemInspectionTempate = [
  'CDOT',
  'CDOT 1176 06/2024',
  'CDOT-1177',
  'CDOT 1177 - 5/2024',
  'Colorado',
  'Delaware',
  'EVP_DUMMY_TEMPLATE',
  'Lennar (Colorado)',
  'Maryland',
  'NCDOT',
  'NCDEQ',
  'Xcel',
];

export const PROJECT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archived' },
];

export function getSchemaLeft(
  clients: Client[],
  inspectionTemplates: InspectionTemplate[],
  states: State[],
  users: any[],
  projectTypes: any[],
  timezones: string[],
  disableClient: boolean = false,
  selectedTemplateId: number | null = null,
  inspectionComplianceRequired: boolean = true,
  optionalComplianceTemplates: string[] = [],
  ccrTemplates: string[] = [],
  project_client: Client,
  precipitation_edit_lock: boolean,
  project_config: ProjectConfig,
  precipitation_flag_tenant_feature: boolean,
) {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    clientId: {
      controlType: UIControlType.select,
      label: 'Client*',
      options: clients,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
      disabled: disableClient,
    },
    permittee: {
      label: 'Permittee',
      description: 'If different than client',
    },
    inspectionTemplateId: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    projectType: {
      controlType: UIControlType.select,
      label: 'Project Type*',
      options: projectTypes,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    isCcrProject: {
      controlType: UIControlType.checkbox,
      label: 'Use CCR Number',
      toolTipOptions: {
        message:
          'If checked the inspector will be required to have a CCR Number that is included on the certification.',
      },
    },
    inspection_compliance_required: {
      controlType: UIControlType.checkbox,
      label: 'Compliance Signature Required',
    },
    findingComplianceRequired: {
      controlType: UIControlType.checkbox,
      label: 'Completed Findings Required For Compliance',
      toolTipOptions: {
        message:
          'When this is selected, compliance signatures are available, even when open or incomplete corrective action findings are present in the inspection.',
      },
    },
    name: {
      label: 'Project Name*',
      validation: { required: true, maxLength: 355 },
      autoComplete: 'none',
    },
    addressStreet: {
      label: 'Project Address',
      placeholder: 'Street',
      className: 'with-spacing',
      'aria-label': 'Street',
      autoComplete: 'address-line1',
    },
    addressCity: {
      placeholder: 'City',
      className: 'with-spacing',
      'aria-label': 'City',
      autoComplete: 'address-level2',
    },
    addressState: {
      controlType: UIControlType.select,
      options: states,
      valueKey: 'id',
      labelKey: env.REACT_APP_REGION === 'UK' ? 'name' : 'abbreviation',
      placeholder:
        env.REACT_APP_REGION === 'UK' ? 'Select Country' : 'Select State',
      autoComplete: 'address-level1',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    addressZip: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Postcode' : 'Zip',
      'aria-label': 'Zip',
      autoComplete: 'postal-code',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
      validation: { format: 'zipcode', maxLength: 5 },
      parse: validator.parseNumber,
    },
    latitude: {
      label: 'Location*',
      placeholder: 'Latitude',
      'aria-label': 'Latitude',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
      // inputMode: "decimal",
      validation: { required: true },
    },
    longitude: {
      placeholder: 'Longitude',
      'aria-label': 'Longitude',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
      // inputMode: "decimal",
      validation: { required: true },
    },
    timezone: {
      controlType: UIControlType.select,
      options:
        env.REACT_APP_REGION === 'UK'
          ? [{ label: 'GMT' }]
          : timezones.map((label) => ({ label })),
      valueKey: 'label',
      labelKey: 'label',
      placeholder: 'Select Timezone',
    },
    inspector_user_id: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
    precipitation_flag: {
      label: 'Precipitation',
      controlType: UIControlType.toggleButton,
      checked: false,
      toggleSize: 'sm',
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    precipitation_threshold: {
      label: `Rain Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    snow_threshold: {
      label: `Snow Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
  };

  // the selected template, if any
  const tmpl: InspectionTemplate = find(inspectionTemplates, {
    id: selectedTemplateId || undefined,
  }) || { name: 'unknown', isUserTemplate: false };

  /**
   * CCR Number option is only available for templates
   * in appContext.ccrTemplates
   */
  if (!ccrTemplates.includes(tmpl.name)) {
    delete schema.isCcrProject;
  }

  /**
   * "compliance required" option
   * only display for selected templates
   */
  if (!selectedTemplateId || !optionalComplianceTemplates.includes(tmpl.name)) {
    delete schema.inspection_compliance_required;
  }

  if (
    !selectedTemplateId ||
    !optionalComplianceTemplates.includes(tmpl.name) ||
    (optionalComplianceTemplates.includes(tmpl.name) &&
      !inspectionComplianceRequired)
  ) {
    delete schema.findingComplianceRequired;
  }

  if (!getProjectConfigHelpers(project_config).hasInAppInspections) {
    delete schema.inspectionTemplateId;
    delete schema.precipitation_flag;
    delete schema.precipitation_threshold;
    delete schema.snow_threshold;
  }

  return schema;
}

export function getSchemaRight(
  docTypes: any[],
  docGroups: any[],
  divisionInspectionIntervals: any[],
  docTypeGroups: any[] = [],
  docTypeGroupdisabled: boolean = false,
  primaryMappingMode: string = '',
  isGISEnabled: boolean = false,
  hubspotFeatureOn: boolean = false,
  hasAnyInspections: boolean = true,
): FormSchema {
  const schema: FormSchema = {
    // break this label into it's own, so it doesn't render as half width
    timeLine: {
      controlType: UIControlType.plainText,
      label: 'Anticipated Project Timeline',
      style: { margin: '0' },
    },
    startDate: {
      controlType: UIControlType.date,
      placeholder: 'Start date',
      'aria-label': 'Project start date',
      style: halfWidthStyle,
      ...(hubspotFeatureOn ? { validation: { required: true } } : {}),
    },
    endDate: {
      controlType: UIControlType.date,
      placeholder: 'End date',
      'aria-label': 'Project end date',
      style: halfWidthStyle,
      className: 'right-popper',
      // popperPlacement: "top-end",
      ...(hubspotFeatureOn ? { validation: { required: true } } : {}),
    },
    // SW2-2152: only shown when the HubSpot Report feature is enabled; required when on.
    ...(hubspotFeatureOn ? {
      actual_award_date: {
        controlType: UIControlType.date,
        label: 'Actual Award Date',
        placeholder: 'Award date',
        'aria-label': 'Actual award date',
        style: halfWidthStyle,
        validation: { required: true },
      },
    } : {}),
    next_inspection_date: {
      controlType: UIControlType.date,
      label: `Next Routine Inspection Date`,
      className: 'right-popper',
      // popperPlacement: "top-end",
      validation: {
        minDate: moment()
          .milliseconds(0)
          .seconds(0)
          .minutes(0)
          .hour(0)
          .toDate(),
      },
      style: emptyDescriptionStyle,
    },
    inspection_interval_id: {
      label: 'Routine Inspection Interval*',
      controlType: UIControlType.select,
      placeholder: 'Select Interval...',
      options: divisionInspectionIntervals,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    notes: {
      label: 'Additional Notes',
      controlType: UIControlType.textarea,
    },
    primary_mapping_mode: {
      label: 'Mapping Mode',
      controlType: UIControlType.select,
      placeholder: 'Select mapping mode...',
      options: [
        { value: 'image', label: 'Image' },
        { value: 'gis', label: 'GIS' },
      ],
      valueKey: 'value',
      labelKey: 'label',
      validation: { required: true },
    },
    image_overlay_enabled: {
      label: 'Image Overlay',
      controlType: UIControlType.checkbox,
      style: { display: primaryMappingMode === 'gis' ? 'block' : 'none' },
    },
    esri_layers_enabled: {
      label: 'ESRI / ArcGIS Layers',
      controlType: UIControlType.checkbox,
      style: { display: primaryMappingMode === 'gis' ? 'block' : 'none' },
    },
    document_group: {
      label: 'Regulations*',
      controlType: UIControlType.select,
      placeholder: 'Select document group...',
      options: docGroups,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    project_document_group: {
      label: 'Project Document Group*',
      controlType: UIControlType.select,
      placeholder: 'Select document type folder group...',
      options: docTypeGroups,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
      disabled: docTypeGroupdisabled,
    },
    docTypes: {
      label: 'Document Types',
      controlType: UIControlType.checkboxGroup,
      options: docTypes,
      valueKey: 'id',
      labelKey: 'name',
      showSelectAll: true,
    },
  };

  if (!isGISEnabled) {
    delete schema.primary_mapping_mode;
  }

  if (!hasAnyInspections) {
    delete schema.next_inspection_date;
    delete schema.inspection_interval_id;
  }

  return schema;
}

export function getProjectInformationSchemaLeft(
  clients: Client[],
  inspectionTemplates: InspectionTemplate[],
  states: State[],
  timezones: string[],
  disableClient: boolean = false,
  selectedTemplateId: number | null = null,
  inspectionComplianceRequired: boolean = true,
  optionalComplianceTemplates: string[] = [],
  ccrTemplates: string[] = [],
  project_config: ProjectConfig,
  clientId: number,
  featureEnableEnhancedFinding: boolean,
  enablePhotoMetadataPermission: boolean,
  enableForecastWeather: boolean,
  enableGuestSignOffs: boolean,
  enableInspectionScheduler: boolean,
  clientInspectionScheduler: boolean,
  clientForecastWeather: boolean,
  forecastWeatherEnabled: boolean,
  areas: any[],
  isProjectSettings: boolean = false,
  primaryMappingMode: string = '',
  isGISEnabled: boolean = false,
): FormSchema {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    clientId: {
      controlType: UIControlType.select,
      label: 'Client*',
      options: clients,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
      disabled: disableClient,
    },
    permittee: {
      label: 'Permittee (If different than client)',
    },
    inspectionTemplateId: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    isCcrProject: {
      controlType: UIControlType.checkbox,
      label: 'Use CCR Number',
      toolTipOptions: {
        message:
          'If checked the inspector will be required to have a CCR Number that is included on the certification.',
      },
    },
    inspection_compliance_required: {
      controlType: UIControlType.checkbox,
      label: 'Compliance Signature Required',
    },
    findingComplianceRequired: {
      controlType: UIControlType.checkbox,
      label: 'Completed Findings Required For Compliance',
      toolTipOptions: {
        message:
          'When this is selected, compliance signatures are available, even when open or incomplete corrective action findings are present in the inspection.',
      },
    },
    name: {
      label: 'Project Name*',
      validation: { required: true, maxLength: 355 },
      autoComplete: 'none',
    },
    addressStreet: {
      label: 'Project Address',
      placeholder: 'Street',
      className: 'with-spacing',
      'aria-label': 'Street',
      autoComplete: 'address-line1',
    },
    addressCity: {
      placeholder: 'City',
      className: 'with-spacing',
      'aria-label': 'City',
      autoComplete: 'address-level2',
    },
    addressState: {
      controlType: UIControlType.select,
      options: states,
      valueKey: 'id',
      labelKey: env.REACT_APP_REGION === 'UK' ? 'name' : 'abbreviation',
      placeholder:
        env.REACT_APP_REGION === 'UK' ? 'Select Country' : 'Select State',
      autoComplete: 'address-level1',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    addressZip: {
      placeholder: env.REACT_APP_REGION === 'UK' ? 'Postcode' : 'Zip',
      'aria-label': 'Zip',
      autoComplete: 'postal-code',
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
      validation: { format: 'zipcode', maxLength: 5 },
      parse: validator.parseNumber,
    },
    latitude: {
      label: 'Location*',
      placeholder: 'Latitude',
      'aria-label': 'Latitude',
      parse: validator.parseDecimal,
      validation: { required: true },
      style: halfWidthStyle,
    },
    longitude: {
      placeholder: 'Longitude',
      'aria-label': 'Longitude',
      parse: validator.parseDecimal,
      validation: { required: true, format: 'longitude' },
      style: halfWidthStyle,
    },
    timezone: {
      controlType: UIControlType.select,
      options:
        env.REACT_APP_REGION === 'UK'
          ? [{ label: 'GMT' }]
          : timezones.map((label) => ({ label })),
      valueKey: 'label',
      labelKey: 'label',
      placeholder: 'Select Timezone',
    },
    report_type: {
      label: 'Report Type',
      controlType: UIControlType.select,
      options: [
        { id: 'entire_inspection', name: 'Entire Inspection' },
        {
          id: 'entire_inspection_wlocations',
          name: 'Entire Inspection W/Finding Location',
        },
        {
          id: 'entire_inspection_wlocations_wphotos',
          name: 'Entire Inspection W/Finding Location and Photos',
        },
        { id: 'ca_log_wophotos', name: 'Findings Log - Without Photos' },
        { id: 'ca_log_wphotos', name: 'Findings Log - With Photos' },
        { id: 'findings_wphotos', name: 'Open Findings Report - With Photos' },
      ],
      labelKey: 'name',
      valueKey: 'id',
      toolTipOptions: {
        message:
          'Select a report type that will be automatically sent out when an inspection report is completed',
      },
    },
    cgp_tracking_id: {
      label: 'Permit Number',
      style: halfWidthStyle,
    },
    current_acreage: {
      label: 'Current Acreage',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
    },
    work_order: {
      label: 'Work Order #',
      style: halfWidthStyle,
    },
    purchase_order: {
      label: 'Purchase Order #',
      style: halfWidthStyle,
    },
    area_id: {
      label: 'Area',
      controlType: UIControlType.select,
      options: areas,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Select Area',
      style: halfWidthStyle,
    },
    primary_mapping_mode: {
      label: 'Mapping Mode',
      controlType: UIControlType.select,
      placeholder: 'Select mapping mode...',
      options: [
        { value: 'image', label: 'Image' },
        { value: 'gis', label: 'GIS' },
      ],
      valueKey: 'value',
      labelKey: 'label',
      validation: { required: true },
    },
    image_overlay_enabled: {
      label: 'Image Overlay',
      controlType: UIControlType.checkbox,
      style: { display: primaryMappingMode === 'gis' ? 'block' : 'none' },
    },
    esri_layers_enabled: {
      label: 'ESRI / ArcGIS Layers',
      controlType: UIControlType.checkbox,
      style: { display: primaryMappingMode === 'gis' ? 'block' : 'none' },
    },
    finding_metric_tracking: {
      controlType: UIControlType.checkbox,
      label: 'Enable Enhanced Finding',
      toolTipOptions: {
        message:
          'When enabled, findings may be tied to specific legend items available for use within site map editor. This data will then be used for reporting.',
      },
    },
    dissolve_finding_images: {
      controlType: UIControlType.checkbox,
      label: 'Dissolving Photos',
      toolTipOptions: {
        message:
          '“When enabled, finding photos will be permanently deleted from the system once a date completed has been entered/the finding has been closed” These photos will not be able to be retrieved once they are deleted.',
      },
    },
    enable_photo_metadata_requirement: {
      controlType: UIControlType.checkbox,
      label: 'Enable Photo Metadata Requirement',
    },
    forecast_weather: {
      controlType: UIControlType.checkbox,
      label: 'NOAA Weather Table',
      toolTipOptions: {
        message: `When enabled the NOAA Weather Table will be imported and included on each inspection reprot. This table will display the 7 day forcast in 6 hour intervals.`,
      },
    },
    guest_sign_offs: {
      label: 'Guest Sign-Offs',
      controlType: UIControlType.checkbox,
      style: { display: enableGuestSignOffs ? 'block' : 'none' },
    },
    ca_inspection_scheduler: {
      controlType: UIControlType.checkbox,
      label: 'CA - Inspection Scheduler',
      style: {
        display:
          enableInspectionScheduler &&
          clientInspectionScheduler &&
          clientForecastWeather &&
          forecastWeatherEnabled
            ? 'block'
            : 'none',
      },
      disabled: !clientForecastWeather,
    },
  };
  const tmpl: InspectionTemplate = find(inspectionTemplates, {
    id: selectedTemplateId || undefined,
  }) || { name: 'unknown', isUserTemplate: false };

  if (!ccrTemplates.includes(tmpl.name)) {
    delete schema.isCcrProject;
  }

  if (!selectedTemplateId || !optionalComplianceTemplates.includes(tmpl.name)) {
    delete schema.inspection_compliance_required;
  }

  if (
    !selectedTemplateId ||
    !optionalComplianceTemplates.includes(tmpl.name) ||
    (optionalComplianceTemplates.includes(tmpl.name) &&
      !inspectionComplianceRequired)
  ) {
    delete schema.findingComplianceRequired;
  }

  if (!getProjectConfigHelpers(project_config).hasInAppInspections) {
    delete schema.inspectionTemplateId;
  }

  // Hide specific fields for Project Settings tab
  if (isProjectSettings) {
    delete schema.inspectionTemplateId;
    // Move these fields to right schema for Project Settings
    delete schema.cgp_tracking_id;
    delete schema.current_acreage;
    delete schema.work_order;
    delete schema.purchase_order;
  }

  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.finding_metric_tracking === false ||
    !featureEnableEnhancedFinding
  ) {
    delete schema.finding_metric_tracking;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.dissolve_finding_images === false
  ) {
    delete schema.dissolve_finding_images;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.enable_photo_metadata_requirement === false ||
    !enablePhotoMetadataPermission
  ) {
    delete schema.enable_photo_metadata_requirement;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.forecast_weather === false ||
    !enableForecastWeather
  ) {
    delete schema.forecast_weather;
  }
  if (
    clients.find((client) => String(client.id) === String(clientId))
      ?.lock_report_type ||
    systemInspectionTempate.includes(tmpl.name)
  ) {
    delete schema.report_type;
  }
  if (
    !enableInspectionScheduler ||
    !clientInspectionScheduler ||
    !clientForecastWeather
  ) {
    delete schema.ca_inspection_scheduler;
  }

  if (!isGISEnabled) {
    delete schema.primary_mapping_mode;
    delete schema.image_overlay_enabled;
    delete schema.esri_layers_enabled;
  }

  return schema;
}

export function getProjectInformationSchemaRight(
  users: any[],
  primaryContactUsers: any[],
  projectTypes: any[],
  divisionInspectionIntervals: any[],
  docGroups: any[],
  isProjectSettings: boolean = false,
  hubspotFeatureOn: boolean = false,
  hasAnyInspections: boolean = true,
): FormSchema {
  ReactTooltip.rebuild();

  const schema: FormSchema = {
    timeLine: {
      controlType: UIControlType.plainText,
      label: 'Anticipated Project Timeline',
      style: { margin: '0' },
    },
    startDate: {
      controlType: UIControlType.date,
      placeholder: 'Start date',
      'aria-label': 'Project start date',
      style: halfWidthStyle,
      ...(hubspotFeatureOn ? { validation: { required: true } } : {}),
    },
    endDate: {
      controlType: UIControlType.date,
      placeholder: 'End date',
      'aria-label': 'Project end date',
      style: halfWidthStyle,
      className: 'right-popper',
      ...(hubspotFeatureOn ? { validation: { required: true } } : {}),
    },
    // SW2-2152: only shown when the HubSpot Report feature is enabled; required when on.
    ...(hubspotFeatureOn ? {
      actual_award_date: {
        controlType: UIControlType.date,
        label: 'Actual Award Date',
        placeholder: 'Award date',
        'aria-label': 'Actual award date',
        style: halfWidthStyle,
        validation: { required: true },
      },
    } : {}),
    next_inspection_date: {
      controlType: UIControlType.date,
      label: `Next Routine Inspection Date`,
      validation: {
        minDate: moment()
          .milliseconds(0)
          .seconds(0)
          .minutes(0)
          .hour(0)
          .toDate(),
      },
      style: { ...halfWidthStyle },
    },
    inspection_interval_id: {
      label: 'Routine Inspection Interval*',
      controlType: UIControlType.select,
      placeholder: 'Select Interval...',
      options: divisionInspectionIntervals,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
    projectType: {
      controlType: UIControlType.select,
      label: 'Project Type*',
      options: projectTypes,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    notes: {
      label: 'Additional Notes',
      controlType: UIControlType.textarea,
      style: { ...halfWidthStyle, alignSelf: 'flex-start' },
    },
    primary_contact: {
      label: 'Primary Contact',
      controlType: UIControlType.customTagSelect,
      isMulti: false,
      openOnFocus: true,
      canSelectCustomValue: true,
      options: primaryContactUsers,
      disabled: primaryContactUsers.length === 0,
      valueKey: 'name',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
    },
    inspector_user_id: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
    document_group: {
      label: 'Regulations*',
      controlType: UIControlType.select,
      placeholder: 'Select document group...',
      options: docGroups,
      valueKey: 'id',
      labelKey: 'name',
      validation: { required: true },
    },
  };

  if (!isProjectSettings && !hasAnyInspections) {
    delete schema.next_inspection_date;
    delete schema.inspection_interval_id;
  }

  // Hide specific fields for Project Settings tab
  if (isProjectSettings) {
    delete schema.next_inspection_date;
    delete schema.inspection_interval_id;
    delete schema.inspector_user_id;

    // Add fields moved from left schema for Project Settings
    schema.cgp_tracking_id = {
      label: 'Permit Number',
      style: halfWidthStyle,
    };
    schema.current_acreage = {
      label: 'Current Acreage',
      style: halfWidthStyle,
      parse: validator.parseDecimal,
    };
    schema.work_order = {
      label: 'Work Order #',
      style: halfWidthStyle,
    };
    schema.purchase_order = {
      label: 'Purchase Order #',
      style: halfWidthStyle,
    };
  }

  return schema;
}

export function getDocumentType(docTypes: any[], docGroups: any[], disableDocTypeGroup:boolean): FormSchema {
  return {
    project_document_group: {
      label: 'Project Document Group',
      controlType: UIControlType.select,
      placeholder: 'Select document group...',
      options: docGroups,
      valueKey: 'id',
      labelKey: 'name',
       validation: {
        required: true,
      },
     disabled: disableDocTypeGroup,
     style: { flex: 'none' },

    },
    docTypes: {
      label: 'Document Types',
      controlType: UIControlType.checkboxGroup,
      options: docTypes,
      valueKey: 'id',
      labelKey: 'name',
      showSelectAll: true,
    },
  };
}

export function getPrecipitationFields(
  precipitation_edit_lock: boolean,
  project_client: Client,
  precipitation_flag_tenant_feature: boolean,
  canShowTempest: boolean,
  isTempestSelected: boolean,
): FormSchema {
  const schema: FormSchema = {
    precipitation_threshold: {
      label: `Rain Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    snow_threshold: {
      label: `Snow Threshold ${
        process.env.REACT_APP_REGION
          ? process.env.REACT_APP_REGION === 'US'
            ? '(inches)'
            : '(mm)'
          : ''
      }`,
      validation: { required: false },
      autoComplete: 'none',
      parse: validator.parseDecimal,
      style: halfWidthStyle,
      disabled:
        precipitation_edit_lock ||
        !precipitation_flag_tenant_feature ||
        (project_client && !project_client.precipitation_client_project_flag),
    },
    weather_station_type: {
      label: 'Weather Station',
      controlType: UIControlType.select,
      options: [
        { value: 'tomorrow.io', label: 'Tomorrow.io' },
        { value: 'tempest', label: 'Tempest' },
      ],
      style: {
        flex: 'none',
        flexBasis: '50%',
      },
    },
    tempest_station_id: {
      label: 'Tempest Station Id',
      controlType: UIControlType.text,
      toolTipOptions: {
        message:
          'This station ID is assigned by Tempest when you register your station on their website. It is a combination of letters and numbers that can be found in the URL of your station page. For example, if your station URL is https://tempestwx.com/station/ABCD1234, then your station ID is ABCD1234.',
      },
      validation: {
        required: isTempestSelected,
      },
      style: halfWidthStyle,
    },
    tempest_station_name: {
      label: 'Tempest Station Name',
      controlType: UIControlType.text,
      disabled: true,
      toolTipOptions: {
        message:
          'This is the name of your Tempest weather station as registered on their website. This field is for reference only and cannot be edited here.',
      },
      style: {
        flex: 'none',
        flexBasis: '50%',
      },
    },
    tempest_latitude: {
      label: 'Station Latitude',
      controlType: UIControlType.text,
      disabled: true,
      style: {
        flex: 'none',
        flexBasis: '25%',
      },
      toolTipOptions: {
        message:
          'Latitude coordinate of the Tempest weather station. This field is for reference only and cannot be edited here.',
      },
    },
    tempest_longitude: {
      label: 'Station Longitude',
      controlType: UIControlType.text,
      disabled: true,
      style: {
        flex: 'none',
        flexBasis: '25%',
      },
      toolTipOptions: {
        message:
          'Longitude coordinate of the Tempest weather station. This field is for reference only and cannot be edited here.',
      },
    },
  };

  if (
    project_client.weather_station_type !== 'tempest' ||
    project_client.is_tempest_locked ||
    !canShowTempest
  ) {
    delete schema.weather_station_type;
  }

  if (
    project_client.weather_station_type !== 'tempest' ||
    !canShowTempest ||
    !isTempestSelected
  ) {
    delete schema.tempest_station_id;
    delete schema.tempest_station_name;
    delete schema.tempest_latitude;
    delete schema.tempest_longitude;
  }

  return schema;
}

export function getSchemaInspectionCertification(
  isEnabled: boolean,
  isSameForBoth: boolean,
  selectedcertificationType: string = 'compliance',
) {
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    certification_type: {
      label: 'Certification Type',
      controlType: UIControlType.select,
      labelKey: 'label',
      valueKey: 'value',
      options: [
        { value: 'compliance', label: 'Compliance' },
        { value: 'acknowledgement', label: 'Acknowledgement' },
        { value: 'both', label: 'Both' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: '33%',
      },
    },
    no_of_days_compliance: {
      label: 'Due In (Compliance)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    no_of_days_acknowledgment: {
      label: 'Due In (Acknowledgement)',
      controlType: UIControlType.number,
      toolTipOptions: {
        message:
          'Set the number of days after an inspection is submitted by which this certification must be signed',
      },
      style: {
        flexBasis: '33%',
      },
      validation: {
        required: false,
      },
      disabled: true,
    },
    sameForBoth: {
      label: 'Same for both',
      controlType: UIControlType.checkbox,
      style: {
        flexBasis: '100%',
        alignSelf: 'center',
        display: 'none',
      },
    },
  };

  if (['compliance', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_compliance.validation) {
      schema.no_of_days_compliance.validation.required = true;
    }
    schema.no_of_days_compliance.disabled = false;
  }

  if (['acknowledgement', 'both'].includes(selectedcertificationType)) {
    if (schema.no_of_days_acknowledgment.validation) {
      schema.no_of_days_acknowledgment.validation.required = true;
    }
    schema.no_of_days_acknowledgment.disabled = false;
  }

  if (isEnabled && selectedcertificationType === 'both') {
    schema.sameForBoth.style!.display = 'block';
  }

  if (isSameForBoth && selectedcertificationType === 'both') {
    schema.no_of_days_compliance.label = 'Due In';
    if (schema.no_of_days_compliance.toolTipOptions) {
      schema.no_of_days_compliance.toolTipOptions.message =
        'Set the number of days after an inspection is submitted by which both the certifications must be signed';
    }
    delete schema.no_of_days_acknowledgment;
  }

  if (!isEnabled) {
    delete schema.certification_type;
    delete schema.no_of_days_compliance;
    delete schema.no_of_days_acknowledgment;
  }

  return schema;
}

export function getSchemaPDFEditing(){
  ReactTooltip.rebuild();
  const schema: FormSchema = {
    append_only_enabled: {
      label: 'Enable PDF Annotation Restriction',
      controlType: UIControlType.checkbox,
      className: 'responsive-form-checkbox',
      toolTipOptions: {
        message:
          'When enabled, PDF editing is limited to the active user session, ensuring that all previously added updates remain locked and cannot be altered or removed.',
      },
    },
  };
  return schema;
}

export function getSchedularFields(schemaData: any): FormSchema {
  // Build inspection type options from API data, merging with defaults and removing duplicates
  const inspectionTypeOptions = (() => {
    const templateTypes = Array.isArray(schemaData.inspectionTypesData?.inspectionTypes)
      ? schemaData.inspectionTypesData.inspectionTypes
      : [];
    
    const defaultTypes = schemaData.defaultInspectionTypeOptions || [];
    
    // Merge template types with defaults, removing duplicates
    const allTypes = Array.from(new Set([...templateTypes, ...defaultTypes]));
    
    return allTypes.map((type: string, index: number) => ({
      id: index + 1,
      value: type,
    }));
  })();

  // Determine placeholder text for inspection type field
  const getInspectionTypePlaceholder = () => {
    if (!schemaData.selectedTemplate) {
      return 'Select an inspection form type first';
    }
    if (schemaData.isLoadingInspectionTypes) {
      return 'Loading inspection types...';
    }
    if (inspectionTypeOptions.length === 0) {
      return 'No inspection types available';
    }
    return 'Select inspection type';
  };

  return {
    schedule_template: {
      controlType: UIControlType.select,
      label: 'Inspection Form Type*',
      options: schemaData.inspectionTemplates,
      valueKey: 'id',
      labelKey: 'name',
      parse: Number.parseInt,
      validation: { required: true },
    },
    schedule_date: {
      label: 'Schedule Date*',
      controlType: UIControlType.date,
      validation: {
        required: true,
        minDate: new Date(),
      },
    },
    schedule_type: {
      label: 'Inspection Type*',
      controlType: UIControlType.select,
      validation: { required: true },
      options: inspectionTypeOptions,
      labelKey: 'value',
      valueKey: 'value',
      placeholder: getInspectionTypePlaceholder(),
      disabled: !schemaData.selectedTemplate || schemaData.isLoadingInspectionTypes || inspectionTypeOptions.length === 0,
    },
    schedule_inspector: {
      label: 'Inspector*',
      controlType: UIControlType.select,
      options: schemaData.users,
      valueKey: 'id',
      labelKey: 'name',
      placeholder: 'Please select a user',
      noOptionMessage: 'There are no users available.',
      validation: { required: true },
    },
    schedule_notes: {
      label: 'Notes',
      controlType: UIControlType.textarea,
      placeholder: 'Add any additional notes for this inspection...',
      validation: { required: false },
    },
  };
}
